import { useQuery } from '@apollo/client';
import Button from '@components/Button';
import { FlexRow } from '@components/FlexRow';
import Icons from '@components/Icons';
import Text from '@components/Text';
import Colors from '@constants/Colors';
import { Card, CardContent, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, useMediaQuery } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useSaveJobOffer } from '../hooks/useSaveJobOffer';

import PreLoader from '@components/PreLoader';
import { GET_PRO } from '@gql/pros';
import { useNavigate } from 'react-router-dom';
import CandidateForm from './CandidateForm';
import ContractForm from './ContractForm';
import DescriptionForm from './DescriptionForm';
import JobOfferProfileForm from './JobOfferProfileForm';
import PositionForm from './PositionForm';
import RecruitmentForm from './RecruitmentForm';

function JobOfferForm(props) {
    const navigate = useNavigate();
    const [step, setStep] = useState<number>(0);
    const { data: proData, loading: fetchingPro } = useQuery(GET_PRO, {
        variables: {
            proId: props.proId,
        },
        skip: !props.proId,
    });

    const pro = proData?.getPro;

    const { submit, loading: saveLoading, success, data, methods } = useSaveJobOffer({
        proId: props.proId,
        jobOffer: props?.jobOffer,
        onSuccess: () => {
            props.onSuccess?.(getValues());
        },
        onError: error => {
            props.onError?.(error);
        },
    });

    const { handleSubmit, getValues, trigger, setValue } = methods;

    const invalidHandler = () => {
        (async function () {
            await validate([0, 1, 2, 3, 4]);
        })();
    };

    const [showAlertPremium, setShowAlertPremium] = useState(false);

    const submitHandler = async (formData: any) => {
        await submit(formData);
    };

    const validate = useCallback(
        async steps => {
            let result = false;
            if (steps.includes(0)) {
                result = await trigger(['title', 'address', 'city', 'publishDate', 'position'], {
                    shouldFocus: true,
                });
                if (!result) {
                    setStep(0);
                    return;
                }
            }
            if (steps.includes(1)) {
                result = await trigger('candidate', { shouldFocus: true });
                if (!result) {
                    setStep(1);
                    return;
                }
            }
            if (steps.includes(2)) {
                result = await trigger('contract', { shouldFocus: true });

                if (!result) {
                    setStep(2);
                    return;
                }
            }
            if (steps.includes(3)) {
                result = await trigger('description', { shouldFocus: true });
                if (!result) {
                    setStep(3);
                    return;
                }
            }
            if (steps.includes(4)) {
                result = await trigger('recruitment', { shouldFocus: true });
                if (!result) {
                    setStep(4);
                    return;
                }
            }
            return result;
        },
        [trigger],
    );

    const isMobile = useMediaQuery('(max-width:600px)');

    useEffect(() => {
        if (!pro) return;

        setValue('postalCode', props?.jobOffer?.postalCode ?? pro.postal);
        setValue('address', props?.jobOffer?.address ?? pro.address);
        setValue('city', props?.jobOffer?.city ?? pro.city);

        // if it's a new job offer, set default self assignee with new struktur
        if (!props?.jobOffer?._id && props?.jobOffer?.assignees?.length === 0) {
            // Convert pro to new assignee format
            const defaultAssignee = {
                _id: pro?._id,
                assigneeType: 'Pro',
                role: pro?.role || 'PRO',
                assigneeDetails: {
                    _id: pro?._id,
                    email: pro?.email,
                    bossFirstname: pro?.bossFirstname,
                    bossLastname: pro?.bossLastname,
                    bossPhoto: pro?.bossPhoto,
                    commercialName: pro?.commercialName,
                    role: pro?.role || 'PRO',
                },
            };

            // Also set legacy format for form compatibility
            setValue('assignees', [defaultAssignee]);
        }
    }, [
        pro,
        props?.jobOffer?.address,
        props?.jobOffer?._id,
        props?.jobOffer?.assignees,
        props?.jobOffer?.city,
        props?.jobOffer?.postalCode,
        setValue,
    ]);

    return (
        <FormProvider {...methods}>
            <div
                style={{
                    flex: 1,
                }}>
                {fetchingPro && <PreLoader />}
                {/* Dialog to confirm after saving premium method, it cannot be changed on edit */}
                <Dialog
                    open={showAlertPremium}
                    onClose={() => {
                        setShowAlertPremium(false);
                    }}
                    PaperProps={{
                        style: {
                            borderColor: '#BDAE40',
                            borderWidth: 4,
                            borderStyle: 'solid',
                            borderRadius: 24,
                        },
                        sx: {
                            p: {
                                md: 4,
                                xs: 2,
                            },
                        },
                    }}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description">
                    <DialogTitle id="alert-dialog-title">
                        <IconButton
                            style={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                backgroundColor: Colors.NEUTRAL_LIGHT_GRAY,
                            }}
                            onClick={() => {
                                setShowAlertPremium(false);
                            }}>
                            <Icons name="close" size={24} color={Colors.NEUTRAL_BLACK} />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack gap={2}>
                            <Text darkGray size={isMobile ? 14 : 16}>
                                Une fois l'offre enregistrée avec l'option Premium afin qu'Upercut se charge du
                                recrutement, il vous sera possible de :
                            </Text>
                            <Stack gap={1}>
                                <Stack flexDirection={'row'} gap={2} alignItems="center">
                                    <i className="fi-br-pencil" style={{ color: Colors.NEUTRAL_DARK_GRAY }} />
                                    <Text dark medium size={isMobile ? 16 : 18}>
                                        Modifier le contenu de l'offre
                                    </Text>
                                </Stack>
                                <Stack flexDirection={'row'} gap={2} alignItems="center">
                                    <i className="fi-br-trash" style={{ color: Colors.NEUTRAL_DARK_GRAY }} />
                                    <Text dark medium size={isMobile ? 16 : 18}>
                                        Supprimer l'offre
                                    </Text>
                                </Stack>
                            </Stack>
                            <Text darkGray size={isMobile ? 14 : 16}>
                                Il ne sera plus possible de :
                            </Text>
                            <Stack>
                                <Stack flexDirection={'row'} gap={2} alignItems="center">
                                    <i className="fi fi-br-pen-slash" style={{ color: Colors.NEUTRAL_DARK_GRAY }} />
                                    <Text dark medium size={isMobile ? 16 : 18}>
                                        Modifier le contenu de l'offre
                                    </Text>
                                </Stack>
                            </Stack>
                            <div
                                style={{
                                    alignSelf: 'center',
                                }}>
                                <Button
                                    onClick={e => {
                                        handleSubmit(submitHandler, invalidHandler)(e);
                                        setShowAlertPremium(false);
                                    }}
                                    variant="contained">
                                    J'ai compris
                                </Button>
                            </div>
                            <Text darkGray size={isMobile ? 10 : 12}>
                                * En cas de suppression de l'offre Premium; tout candidat présenté par Upercut qui
                                serait recruté par l'entreprise, reste du à Upercut.
                            </Text>
                        </Stack>
                    </DialogContent>
                </Dialog>
                <Stack
                    style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                    }}>
                    {!success ? (
                        <>
                            <Grid container spacing={4} style={{ zIndex: 2, position: 'relative' }}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <PositionForm pro={pro} />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <JobOfferProfileForm pro={pro} />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <CandidateForm />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <ContractForm />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <DescriptionForm />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <RecruitmentForm pro={pro} />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                            <FlexRow
                                justifyContent={'space-between'}
                                style={{ marginTop: 32, zIndex: 0, position: 'relative' }}>
                               
                                        <Button
                                            disabled={props.cancelDisabled}
                                            variant="text"
                                            color="secondary"
                                            startIcon={
                                                <i
                                                    className="fi fi-br-arrow-left"
                                                    style={{
                                                        marginRight: 8,
                                                        fontSize: 18,
                                                        color: Colors.NEUTRAL_BLACK,
                                                    }}
                                                />
                                            }
                                            onClick={() => {
                                                if (step === 0) {
                                                    return;
                                                }
                                                setStep(step - 1);
                                            }}>
                                            Retour
                                        </Button>
                                       <Button
                                        loading={saveLoading}
                                        onClick={e => {
                                            handleSubmit(submitHandler, invalidHandler)(e);
                                        }}>
                                        Enregistrer
                                    </Button>
                                
                            </FlexRow>
                        </>
                    ) : (
                        <Stack
                            style={{
                                paddingTop: 24,
                                gap: 24,
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}>
                            <img
                                src="/images/job-offer-created.svg"
                                alt="Offre d'emploi créée"
                                style={{
                                    width: 195,
                                    height: 190,
                                }}
                            />
                            <Stack style={{ gap: 8, maxWidth: 595 }}>
                                <Text heading dark bold size={isMobile ? 20 : 24} style={{ textAlign: 'center' }}>
                                    Offre d'emploi {props?.jobOffer?._id ? 'modifiée' : 'créée'}
                                </Text>
                                <Text darkGray size={isMobile ? 14 : 16} style={{ textAlign: 'center' }}>
                                    {props?.jobOffer?._id ? (
                                        <>
                                            Votre offre d'emploi a été modifiée avec succès. Maintenant, attendons
                                            avec impatience que les candidats postulent.
                                        </>
                                    ) : (
                                        <>
                                            Félicitations ! Votre offre d'emploi a été créée avec succès.
                                            Maintenant, attendons avec impatience que les candidats postulent.
                                        </>
                                    )}
                                </Text>
                            </Stack>
                            <FlexRow style={{ gap: 12, justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        navigate('/jobs/' + data?.jobNumber);
                                    }}>
                                    Voir l'offre d'emploi
                                </Button>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => {
                                        navigate('/pro/job-offers');
                                    }}>
                                    Retour au dashboard
                                </Button>
                            </FlexRow>
                        </Stack>
                    )}
                </Stack>
            </div>
        </FormProvider>
    );
}

export default JobOfferForm;
